/**
 * SplitPane Component
 * GAP-004: Desktop three-panel layout with resizable panels
 *
 * A resizable split pane component for desktop layouts.
 * Supports horizontal and vertical orientations with drag handles.
 */

import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
  type MouseEvent,
  type TouchEvent,
} from 'react';
import { cn } from '../../utils/cn';

export interface SplitPaneProps {
  /** Primary panel content (left/top) */
  children: [ReactNode, ReactNode];
  /** Orientation of the split */
  orientation?: 'horizontal' | 'vertical';
  /** Default size of the primary panel in pixels */
  defaultSize?: number;
  /** Minimum size of the primary panel in pixels */
  minSize?: number;
  /** Maximum size of the primary panel in pixels */
  maxSize?: number;
  /** Whether the split pane is collapsible */
  collapsible?: boolean;
  /** Whether to persist size to localStorage */
  persistKey?: string;
  /** Callback when size changes */
  onSizeChange?: (size: number) => void;
  /** Custom class for the container */
  className?: string;
  /** Custom class for the primary panel */
  primaryClassName?: string;
  /** Custom class for the secondary panel */
  secondaryClassName?: string;
}

export function SplitPane({
  children,
  orientation = 'horizontal',
  defaultSize = 300,
  minSize = 200,
  maxSize = 600,
  collapsible = false,
  persistKey,
  onSizeChange,
  className,
  primaryClassName,
  secondaryClassName,
}: SplitPaneProps) {
  const [primary, secondary] = children;
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startPos = useRef(0);
  const startSize = useRef(0);

  // Load persisted size or use default
  const [size, setSize] = useState(() => {
    if (persistKey) {
      const stored = localStorage.getItem(`splitpane_${persistKey}`);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed >= minSize && parsed <= maxSize) {
          return parsed;
        }
      }
    }
    return defaultSize;
  });

  const [isCollapsed, setIsCollapsed] = useState(false);

  // Persist size to localStorage
  useEffect(() => {
    if (persistKey && !isCollapsed) {
      localStorage.setItem(`splitpane_${persistKey}`, String(size));
    }
  }, [size, persistKey, isCollapsed]);

  // Handle drag start
  const handleDragStart = useCallback(
    (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      isDragging.current = true;
      startSize.current = size;

      if ('touches' in e) {
        startPos.current = orientation === 'horizontal' ? e.touches[0].clientX : e.touches[0].clientY;
      } else {
        startPos.current = orientation === 'horizontal' ? e.clientX : e.clientY;
      }

      document.body.style.cursor = orientation === 'horizontal' ? 'col-resize' : 'row-resize';
      document.body.style.userSelect = 'none';
    },
    [orientation, size]
  );

  // Handle drag move
  const handleDragMove = useCallback(
    (e: globalThis.MouseEvent | globalThis.TouchEvent) => {
      if (!isDragging.current) return;

      let currentPos: number;
      if ('touches' in e) {
        currentPos = orientation === 'horizontal' ? e.touches[0].clientX : e.touches[0].clientY;
      } else {
        currentPos = orientation === 'horizontal' ? e.clientX : e.clientY;
      }

      const delta = currentPos - startPos.current;
      let newSize = startSize.current + delta;

      // Clamp size
      newSize = Math.max(minSize, Math.min(maxSize, newSize));

      setSize(newSize);
      onSizeChange?.(newSize);
    },
    [orientation, minSize, maxSize, onSizeChange]
  );

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    isDragging.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Set up global event listeners for drag
  useEffect(() => {
    const handleMouseMove = (e: globalThis.MouseEvent) => handleDragMove(e);
    const handleMouseUp = () => handleDragEnd();
    const handleTouchMove = (e: globalThis.TouchEvent) => handleDragMove(e);
    const handleTouchEnd = () => handleDragEnd();

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleDragMove, handleDragEnd]);

  // Toggle collapse
  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  // Handle double-click on divider to collapse/expand
  const handleDoubleClick = useCallback(() => {
    if (collapsible) {
      toggleCollapse();
    }
  }, [collapsible, toggleCollapse]);

  const isHorizontal = orientation === 'horizontal';
  const displaySize = isCollapsed ? 0 : size;

  return (
    <div
      ref={containerRef}
      className={cn(
        'flex h-full',
        isHorizontal ? 'flex-row' : 'flex-col',
        className
      )}
    >
      {/* Primary Panel */}
      <div
        className={cn(
          'flex-shrink-0 overflow-auto transition-all duration-200',
          isCollapsed && 'hidden',
          primaryClassName
        )}
        style={{
          [isHorizontal ? 'width' : 'height']: displaySize,
        }}
      >
        {primary}
      </div>

      {/* Resize Handle */}
      <div
        className={cn(
          'group relative flex-shrink-0 select-none',
          isHorizontal
            ? 'w-1 cursor-col-resize hover:w-2'
            : 'h-1 cursor-row-resize hover:h-2',
          'bg-mv-gray-200 transition-all',
          'hover:bg-mv-blue-400',
          isDragging.current && 'bg-mv-blue-500'
        )}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        onDoubleClick={handleDoubleClick}
        role="separator"
        aria-orientation={isHorizontal ? 'vertical' : 'horizontal'}
        aria-valuenow={displaySize}
        aria-valuemin={minSize}
        aria-valuemax={maxSize}
        tabIndex={0}
        onKeyDown={(e) => {
          const step = e.shiftKey ? 50 : 10;
          if (isHorizontal) {
            if (e.key === 'ArrowLeft') {
              setSize((s) => Math.max(minSize, s - step));
            } else if (e.key === 'ArrowRight') {
              setSize((s) => Math.min(maxSize, s + step));
            }
          } else {
            if (e.key === 'ArrowUp') {
              setSize((s) => Math.max(minSize, s - step));
            } else if (e.key === 'ArrowDown') {
              setSize((s) => Math.min(maxSize, s + step));
            }
          }
        }}
      >
        {/* Visual indicator */}
        <div
          className={cn(
            'absolute opacity-0 transition-opacity group-hover:opacity-100',
            isHorizontal
              ? 'left-1/2 top-1/2 h-8 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-mv-gray-400'
              : 'left-1/2 top-1/2 h-1 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full bg-mv-gray-400'
          )}
        />

        {/* Collapse button (if collapsible) */}
        {collapsible && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleCollapse();
            }}
            className={cn(
              'absolute z-10 flex h-6 w-6 items-center justify-center rounded-full',
              'bg-white border border-mv-gray-300 shadow-sm',
              'hover:bg-mv-gray-50 focus:outline-none focus:ring-2 focus:ring-mv-blue-500',
              'opacity-0 transition-opacity group-hover:opacity-100',
              isHorizontal
                ? 'left-1/2 top-2 -translate-x-1/2'
                : 'left-2 top-1/2 -translate-y-1/2'
            )}
            aria-label={isCollapsed ? 'Uitvouwen' : 'Inklappen'}
          >
            <svg
              className={cn(
                'h-3 w-3 text-mv-gray-500 transition-transform',
                isHorizontal && isCollapsed && 'rotate-180',
                !isHorizontal && isCollapsed && 'rotate-180'
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isHorizontal ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              )}
            </svg>
          </button>
        )}
      </div>

      {/* Secondary Panel */}
      <div className={cn('flex-1 overflow-auto', secondaryClassName)}>
        {secondary}
      </div>
    </div>
  );
}

export default SplitPane;
